<script setup lang="ts">
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import '@patsnap-ui/icon/assets/solid/edit.svg';
import '@patsnap-ui/icon/assets/solid/EyeOff.svg';
import SvgIcon from '@/presentation/components/common/svg-icon/SvgIcon.vue';
import DisclosureTextEditor from './DisclosureTextEditor.vue';
import { CheckSensitiveWordService } from '@/services/common/check-sensitive-word';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { useAiDisclosureResult } from '@/composables/use-ai-disclosure-result';

const checkSensitiveWordService = injectService<CheckSensitiveWordService>(
    CheckSensitiveWordService
);

const {
    aiDisclosureResultService,
    aiDisclosureResultData,
} = useAiDisclosureResult();

const { t } = useI18n();
const isEditing = ref<boolean>(false);
const isCheckingSensitiveWord = ref<boolean>(false);

defineProps<{
    showHover: boolean;
}>();

function handelEdit() {
    isEditing.value = true;
}

function handelEditCancel() {
    isEditing.value = false;
}

async function handleEditSubmit(newText: string) {
    isCheckingSensitiveWord.value = true;
    const res = await checkSensitiveWordService.checkSensitiveWord(newText);
    isCheckingSensitiveWord.value = false;
    if (res) return;

    await aiDisclosureResultService.updateTitle(newText);
    isEditing.value = false;
}
</script>

<template>
    <div
        class="disclosure-title group flex w-[640px] justify-between overflow-y-hidden rounded-lg p-3"
        :class="{'hover:bg-grey-20': showHover && !isEditing } "
    >
        <DisclosureTextEditor
            v-if="isEditing"
            class="disclosure-title-editing w-full"
            :defaultText="aiDisclosureResultData.title"
            :isLoadingSubmit="isCheckingSensitiveWord"
            @cancel="handelEditCancel"
            @submit="handleEditSubmit"
        />

        <div
            v-else
            class="w-[550px] text-xl font-semibold !leading-10 text-black"
        >
            {{ aiDisclosureResultData.title }}
        </div>

        <button
            v-show="!isEditing"
            class="th-btn th-btn-plain hidden cursor-pointer group-hover:flex"
            @click="handelEdit"
        >
            <SvgIcon
                class="th-btn-icon mr-1 h-4 w-4"
                icon="SolidEdit"
            />

            <span>
                {{ t('aiDisclosureGlobal.globalBtn.edit') }}
            </span>
        </button>
    </div>
</template>

<style lang="scss">
.disclosure-title-editing {
    textarea {
        @apply text-xl font-semibold leading-10 text-black;
    }
}
</style>
